import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onTabClick", "onCatalogOptionClick"];
import { View } from 'foundations-components/components/View';
import { H1 } from 'foundations-components/transitional/components/H1';
import { H2 } from 'foundations-components/transitional/components/H2';
import { H4 } from 'foundations-components/transitional/components/H4';
import { H5 } from 'foundations-components/transitional/components/H5';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useUserInfo } from '../../accounts/accountsQueries';
import I18n from 'I18n';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Tag } from 'foundations-components/transitional/components/Tag';
import getLang from '../../../utils/getLang';
import { useCallback, useEffect } from 'react';
import UsageTracker from '../../../utils/UsageTracker';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { getSearchUrl } from '../searchRouting';
import { RouterLink } from '../../../components/AcademyLinks';
import { SCREENS, SUB_SCREENS } from '../../../constants/Tracking';
import { ActionsCollapser } from 'foundations-components/transitional/components/ActionsCollapser';
import HomePageSection from '../../../components/Layouts/HomePageSection';
import { useNavPageHandlers } from '../../navigation';
import { ACCatalogFilterButton } from 'academy-navigation-lib/components/ACCatalog/ACCatalogFilterButton';
import { ACCatalogMultiSelect } from 'academy-navigation-lib/components/ACCatalog/ACCatalogMultiSelect';
import { ACSearchInput } from 'academy-navigation-lib/components/ACSearchInput/ACSearchInput';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const POPULAR_SEARCH_TERMS = {
  en: ['marketing', 'digital marketing', 'sales', 'CRM', 'SEO', 'social media', 'social media marketing', 'email marketing', 'inbound marketing', 'content marketing', 'hubspot', 'AI', 'scheduling', 'accounting'],
  es: ['marketing', 'marketing digital', 'inbound marketing', 'ventas', 'CRM', 'software', 'redes sociales', 'email marketing', 'social media marketing', 'software de ventas', 'SEO', 'marketing de contenido', 'hubspot', 'IA', 'programación', 'contabilidad'],
  fr: ['marketing', 'marketing digital', 'SEO', 'CRM', 'inbound marketing', 'ventes', 'social media', 'email marketing', 'social media marketing', 'hubspot', 'IA', 'marketing hub', 'sales hub'],
  de: ['CRM', 'sales', 'marketing', 'inbound marketing', 'social media', 'SEO', 'digital marketing', 'social media marketing', 'email marketing', 'hubspot', 'AI', 'marketing hub', 'sales hub'],
  ja: ['CRM', 'インバウンド', 'マーケティング', 'デジタルマーケティング', 'SEO', 'ワークフロー'],
  // CRM, inbound, marketing, digital marketing, SEO, workflow
  pt: ['CRM', 'marketing', 'vendas', 'service hub', 'revenue', 'fluxo de trabalho', 'relatórios', 'inbound marketing', 'hubspot', 'AI', 'marketing hub', 'sales hub']
};
const useGetStartedSearchPrompt = () => {
  var _userInfo$user;
  const language = getLang();
  const {
    userInfo,
    userInfoLoading,
    userInfoError
  } = useUserInfo();
  const suggestedKeywords = POPULAR_SEARCH_TERMS[language] || POPULAR_SEARCH_TERMS['en'];
  return {
    userInfoLoading,
    userInfoError,
    firstName: userInfo === null || userInfo === void 0 || (_userInfo$user = userInfo.user) === null || _userInfo$user === void 0 ? void 0 : _userInfo$user.firstName,
    language,
    suggestedKeywords
  };
};
export const GetStartedSearchPrompt = ({
  includeKeywords = true
}) => {
  const {
    isMobile
  } = useMediaQuery();
  const {
    firstName,
    suggestedKeywords
  } = useGetStartedSearchPrompt();
  useEffect(() => {
    UsageTracker.trackStandalone('Pageview', {
      screen: SCREENS.HOME,
      subscreen: SUB_SCREENS.GET_STARTED_SEARCH_PROMPT
    });
  }, []);
  const _useNavPageHandlers = useNavPageHandlers({
      appearsIn: 'get-started-search-prompt'
    }),
    {
      onCatalogOptionClick
    } = _useNavPageHandlers,
    searchInputHandlers = _objectWithoutPropertiesLoose(_useNavPageHandlers, _excluded);
  const handleOnClickSuggestedKeyword = useCallback(keyword => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'get-started-search-prompt-suggested-keyword',
      search: keyword
    });
  }, []);
  const Header = isMobile ? H4 : H1;
  const Subheader = isMobile ? H5 : H2;
  return /*#__PURE__*/_jsx(HomePageSection, {
    backgroundColor: "fill-surface-recessed",
    children: /*#__PURE__*/_jsx(TFlex, {
      gap: 16,
      maxInlineSize: "964px",
      children: /*#__PURE__*/_jsxs(View, {
        flexGrow: 1,
        flexWrap: "wrap",
        children: [/*#__PURE__*/_jsx(Header, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.getStarted.header",
            options: {
              firstName
            }
          })
        }), /*#__PURE__*/_jsx(Subheader, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.getStarted.subHeader"
          })
        }), /*#__PURE__*/_jsxs(View, {
          "data-onboarding-hook": "search-or-browse-topics",
          children: [/*#__PURE__*/_jsxs(TFlex, {
            alignItems: "center",
            direction: isMobile ? 'row-reverse' : 'row',
            gap: 16,
            children: [isMobile ? /*#__PURE__*/_jsx(ACCatalogFilterButton, {
              onCatalogOptionClick: onCatalogOptionClick
            }) : /*#__PURE__*/_jsx(ACCatalogMultiSelect, {
              onCatalogOptionClick: onCatalogOptionClick
            }), /*#__PURE__*/_jsx(View, {
              blockSize: "42px",
              flexGrow: 1,
              position: "relative",
              zIndex: "3",
              children: /*#__PURE__*/_jsx(View, {
                inlineSize: "100%",
                position: "absolute",
                children: /*#__PURE__*/_jsx(ACSearchInput, Object.assign({
                  placeholder: I18n.text('learningCenter.getStarted.placeholder')
                }, searchInputHandlers))
              })
            })]
          }), includeKeywords && /*#__PURE__*/_jsx(View, {
            marginBlockStart: "400",
            children: /*#__PURE__*/_jsx(TFlex, {
              columnGap: "300",
              flexWrap: "wrap",
              rowGap: "1000",
              children: /*#__PURE__*/_jsx(ActionsCollapser, {
                dropdownProps: {
                  buttonUse: 'unstyled',
                  padding: '300'
                },
                children: suggestedKeywords.map(keyword => /*#__PURE__*/_jsx(Tag, {
                  children: /*#__PURE__*/_jsx(RouterLink, {
                    onClick: () => handleOnClickSuggestedKeyword(keyword),
                    to: getSearchUrl({
                      searchTerm: keyword
                    }),
                    typography: "heading-25",
                    use: "unstyled",
                    children: keyword
                  })
                }, keyword))
              })
            })
          })]
        })]
      })
    })
  });
};
export default GetStartedSearchPrompt;